import Button from 'components/Button';
import { DateField, Form, RadioField, SelectField } from 'components/Form';
import { SmartContainer, SmartInline, SmartItem } from 'components/SmartContainer/SmartContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GenericFilterOperationEnum } from 'services/tenantManagementService';
import { additionalFilterOptions, AndOrEnum, findAllFilters } from '../../filtersHelper';
import { DateFilterModel } from './DateFilterModel';
import { DateColumnModel, DateTimeColumnModel, FilterType } from 'components/Table';
import { formatServerDate } from 'utils/dateTimeUtils';

type Props = {
	column: DateColumnModel | DateTimeColumnModel
	filters: FilterType[]
	onSave: (filters: FilterType[]) => void
	onCancel: () => void
}

const filterTypeOptions = [
	{ value: GenericFilterOperationEnum.EQUALS, text: 'i18n.label.filter.operation.equals' },
	{ value: GenericFilterOperationEnum.NOT_EQUALS, text: 'i18n.label.filter.operation.notEquals' },
	{ value: GenericFilterOperationEnum.GREATER_EQUAL_THAN, text: 'i18n.label.filter.operation.greaterEqualThan' },
	{ value: GenericFilterOperationEnum.GREATER_THAN, text: 'i18n.label.filter.operation.greaterThan' },
	{ value: GenericFilterOperationEnum.LESS_EQUAL_THAN, text: 'i18n.label.filter.operation.lessEqualThen' },
	{ value: GenericFilterOperationEnum.LESS_THAN, text: 'i18n.label.filter.operation.lessThan' },
]

export const DateFilter = ({ column, filters, onSave, onCancel }: Props) => {
	const [values, setValues] = useState(new DateFilterModel());

	useEffect(
		() => {
			const newValues = new DateFilterModel();

			const currentFilters = findAllFilters(filters, column.id);
			// if there are two filters that means AND filter is used
			if (currentFilters && currentFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = new Date(currentFilters[0].value);
				newValues.filterType2 = currentFilters[1].operation;
				newValues.filterValue2 = new Date(currentFilters[1].value);
				// if there is one filter, but without OR filters
			} else if (currentFilters && currentFilters.length === 1 && (!currentFilters[0].orFilters || currentFilters[0].orFilters.length === 0)) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = new Date(currentFilters[0].value);
				// if there is one filter, with two OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation
				newValues.filterValue1 = new Date(currentFilters[0].orFilters[0].value);
				newValues.filterType2 = currentFilters[0].orFilters[1].operation
				newValues.filterValue2 = new Date(currentFilters[0].orFilters[1].value);
				// if there is one filter, with one OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 1) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation
				newValues.filterValue1 = new Date(currentFilters[0].orFilters[0].value)
			}

			setValues(newValues);
		},
		[filters, column.id]
	)

	const submitCallback = useCallback(
		async () => {
			let newFilters = [...filters];
			// remove current filters with specific columnId
			const currentFilters = findAllFilters(newFilters, column.id);
			if (currentFilters.length > 0) {
				currentFilters.forEach(filter => {
					const index = newFilters.indexOf(filter);
					newFilters.splice(index, 1);
				})
			}

			const newFilter: FilterType = {};
			// If OR filter is selected, add filters to orFilters array
			if (values.additionalFilter === AndOrEnum.Or) {
				newFilter.orFilters = []
				if (values.filterType1) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: formatServerDate(values.filterValue1)
					})
				}

				if (values.filterType2) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: formatServerDate(values.filterValue2)
					})
				}
				if (newFilter.orFilters.length > 0) {
					newFilters.push(newFilter);
				}
			}
			// If AND filter is selected add "Parent" filter
			if (values.additionalFilter === AndOrEnum.And) {
				if (values.filterType1) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: formatServerDate(values.filterValue1)
					})
				}

				// If second filter is selected add new "Parent" filter
				if (values.filterType2) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: formatServerDate(values.filterValue2)
					})
				}
			}

			onSave && await onSave(newFilters);
		},
		[filters, column.id, onSave, values]
	)

	const clearCallback = useCallback(
		() => {
			setValues(new DateFilterModel());
		},
		[]
	)

	// filter EQUAL and NOT_EQUAL do not work because the column contains date and time, and filter works only for dates, so we remove these options
	const filteredTypeOptions = useMemo(
		() => column instanceof DateTimeColumnModel ? filterTypeOptions.slice(2) : filterTypeOptions,
		[column]
	)

	return (
		<Form
			values={values}
			onChange={setValues}
			onSubmit={submitCallback}
			onCancel={onCancel}
			submitButtonText='i18n.button.apply'
			disableUnsavedChangesGuard
			render={() => (
				<SmartContainer>
					<SmartItem>
						<SmartInline>
							<SelectField
								id='filterType1'
								items={filteredTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<DateField
								id='filterValue1'
								inline
							/>
						</SmartInline>
						<RadioField
							id='additionalFilter'
							items={additionalFilterOptions}
						/>
						<SmartInline>
							<SelectField
								id='filterType2'
								items={filteredTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<DateField
								id='filterValue2'
								inline
							/>
						</SmartInline>
					</SmartItem>
				</SmartContainer>
			)}
			renderAdditionalButtons={() => (
				<Button
					text='i18n.button.clear'
					color='destructive'
					onClick={clearCallback}
				/>
			)}
		/>
	)
}
